// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    showTitleIcon?: boolean;
    showBottomBorder?: boolean;
    inNewTab?: boolean;
    variant?: keyof ThemeTypography;
    title?: string;
    buttonTitle?: string;
    buttonLink?: string;
    buttonSize?: "small" | "large";
    buttonType?: "link" | "button";
    buttonAlignment?: "left" | "right";
    onClick?: () => void;
    titleContentSpace?: number;
    extendedStyles?: StyledTitleStyles;
}

type Props = OwnProps;

export interface StyledTitleStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    container?: InjectableCss;
    link?: LinkPresentationProps;
    smallLink?: LinkPresentationProps;
    leftSide?: InjectableCss;
}

export const styledTitleStyles: StyledTitleStyles = {};

const StyledTitle: FC<Props> = ({
    showTitleIcon,
    showBottomBorder,
    inNewTab,
    variant,
    title,
    buttonTitle,
    buttonLink,
    buttonType = "link",
    buttonSize = "large",
    buttonAlignment = "right",
    onClick,
    titleContentSpace,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(styledTitleStyles, extendedStyles));
    const history = useHistory();

    const navigate = () => {
        onClick?.();

        if (inNewTab) {
            window.open(buttonLink, "_blank", "noreferrer");
        } else if (buttonLink?.startsWith("http")) {
            window.location.href = buttonLink;
        } else if (buttonLink) {
            history.push(buttonLink);
        }
    };

    return (
        <StyledWrapper {...styles.container} {...{ showBottomBorder, buttonAlignment, titleContentSpace }}>
            <StyledWrapper {...styles.leftSide}>
                <Typography variant={variant} as="h1" {...styles.title} {...{ showTitleIcon }}>
                    {title}
                </Typography>
            </StyledWrapper>
            {!!buttonTitle && !!buttonLink && buttonType === "link" && (
                <Link {...(buttonSize === "small" ? styles.smallLink : styles.link)} onClick={navigate}>
                    {buttonTitle}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default StyledTitle;
